import React, { useEffect, useState } from 'react';
import {
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Alert,
  LinearProgress,
  Divider,
  Stack,
  Switch,
  Tab,
  Tabs,
} from '@mui/material';
import {
  AltRoute,
  Autorenew,
  Cancel,
  CheckCircle,
  Gavel,
  HelpOutline,
  Info,
  Policy,
  Rule as RuleIcon,
  Search,
} from '@mui/icons-material';

import { CreditRiskAPI, formatPercentage } from '../services/api';
import SumaRefinanceList from './SumaRefinanceList';
import {
  InputCatalogues,
  RefinanceEligibilityRule,
  RefinanceInputData,
  RefinanceLookupResult,
  RefinancePriorLoan,
  RefinanceResult,
  RefinanceRuleBook,
  RefinanceRuleKind,
  RefinanceRuleStatus,
  RefinanceTier,
} from '../types';

interface TierPresentation {
  label: string;
  meaning: string;
  severity: 'success' | 'warning' | 'error' | 'info';
  color: string;
  icon: React.ReactElement;
  // Shown in place of the score widget. Rendering an empty gauge instead
  // would read as a probability of zero next to a refusal.
  noScore: string;
}

const TIER_PRESENTATION: Record<RefinanceTier, TierPresentation> = {
  ELEGIBLE_PREFERENTE: {
    label: 'Elegible preferente',
    meaning: 'Todas las reglas se cumplen. Puede ofrecerse el refinanciamiento.',
    severity: 'success',
    color: '#2e7d32',
    icon: <CheckCircle />,
    noScore:
      'Las reglas permiten la oferta, pero el modelo no devolvió una '
      + 'probabilidad. Revise las advertencias de entrada.',
  },
  ELEGIBLE_CON_REVISION: {
    label: 'Elegible con revisión',
    meaning:
      'Falta una regla de maduración. La decisión pasa a un oficial, no la '
      + 'toma el sistema.',
    severity: 'warning',
    color: '#ed6c02',
    icon: <Gavel />,
    noScore:
      'Las reglas permiten la oferta, pero el modelo no devolvió una '
      + 'probabilidad. Revise las advertencias de entrada.',
  },
  NO_ELEGIBLE: {
    label: 'No elegible',
    meaning:
      'Falló una exclusión, o dos o más reglas de maduración. No se ofrece '
      + 'refinanciamiento.',
    severity: 'error',
    color: '#d32f2f',
    icon: <Cancel />,
    noScore:
      'No se calcula probabilidad para un cliente que las reglas rechazan. '
      + 'La exclusión es absoluta y no se revierte con un número.',
  },
  REQUIERE_DATOS: {
    label: 'Requiere datos',
    meaning:
      'Es un faltante de información, no un rechazo. Una exclusión no pudo '
      + 'verificarse, y el sistema no aprueba sobre un campo ausente.',
    severity: 'info',
    color: '#0288d1',
    icon: <HelpOutline />,
    noScore:
      'No se calcula probabilidad mientras una exclusión no pueda '
      + 'verificarse. Complete el dato faltante y vuelva a evaluar.',
  },
  FUERA_DE_ALCANCE: {
    label: 'Fuera de alcance',
    meaning:
      'El cliente no tiene crédito anterior, así que esto no es una pregunta '
      + 'de refinanciamiento sino de scorecard de nuevo cliente. No es un '
      + 'rechazo.',
    severity: 'info',
    color: '#5e35b1',
    icon: <AltRoute />,
    noScore:
      'El modelo de refinanciamiento compara contra un crédito anterior, y '
      + 'aquí no hay ninguno. Evalúe al cliente con el scorecard de nuevos '
      + 'clientes.',
  },
};

const UNKNOWN_TIER: TierPresentation = {
  label: 'Resultado no reconocido',
  meaning: 'El API devolvió un nivel que esta pantalla no conoce.',
  severity: 'info',
  color: '#616161',
  icon: <Info />,
  noScore: 'Sin probabilidad para este nivel.',
};

const ACTION_LABELS: Record<string, string> = {
  OFRECER: 'Ofrecer',
  REVISION_DE_OFICIAL: 'Revisión de oficial',
  NO_OFRECER: 'No ofrecer',
  COMPLETAR_DATOS: 'Completar datos',
  EVALUAR_CON_SCORECARD: 'Evaluar con scorecard',
};

interface KindPresentation {
  label: string;
  explanation: string;
  color: string;
}

const KIND_PRESENTATION: Record<RefinanceRuleKind, KindPresentation> = {
  ALCANCE: {
    label: 'Alcance',
    explanation: 'Define si la consulta es de refinanciamiento.',
    color: '#5e35b1',
  },
  EXCLUSION: {
    label: 'Exclusión',
    explanation: 'Absoluta: ningún buen comportamiento la compensa.',
    color: '#d32f2f',
  },
  MADURACION: {
    label: 'Maduración',
    explanation:
      'Ordenamiento de riesgo: una sola faltante pasa a revisión de oficial, '
      + 'dos o más es rechazo.',
    color: '#ed6c02',
  },
};

const KIND_ORDER: RefinanceRuleKind[] = ['ALCANCE', 'EXCLUSION', 'MADURACION'];

interface StatusPresentation {
  label: string;
  color: string;
  icon: React.ReactElement;
  dashed: boolean;
}

const STATUS_PRESENTATION: Record<RefinanceRuleStatus, StatusPresentation> = {
  CUMPLE: {
    label: 'Cumple',
    color: '#2e7d32',
    icon: <CheckCircle fontSize="small" />,
    dashed: false,
  },
  NO_CUMPLE: {
    label: 'No cumple',
    color: '#d32f2f',
    icon: <Cancel fontSize="small" />,
    dashed: false,
  },
  // "No se pudo verificar" is not "falló", so it is drawn as an open question
  // rather than as a failure.
  NO_VERIFICABLE: {
    label: 'No verificable',
    color: '#0288d1',
    icon: <HelpOutline fontSize="small" />,
    dashed: true,
  },
};

const DEFAULT_PRIOR_LOAN: RefinancePriorLoan = {
  amount: 9000,
  term: 24,
  rate: 19.0,
  product: 'SUMA_CONFIANZA',
  start: '2024-03-15T00:00:00',
  status: 'VIGENTE',
  first_instalment_late: false,
};

// The verified example from RefinanceInput.Config.schema_extra. The category
// values were checked against all three model artifacts, which do not cover
// the same period, so a substitution that looks equivalent can be unknown to
// this model.
const DEFAULT_FORM: RefinanceInputData = {
  Edad: 42,
  Estado_Civil: 'CASADO',
  Monto_Credito: 12000,
  Plazo: 36,
  Tasa_Fija: 18.0,
  Ingreso_Mensual: 2500,
  Producto: 'SUMA_CONFIANZA',
  Destino_Credito: 'CAPITAL_DE_TRABAJO',
  Sector_General: 'MICRO_EMPRESA',
  Sector_Economico: 'COMERCIO',
  Sucursal_Grupo: '7-AG._DAVID',
  Tipo_Persona: 'NATURAL',
  Tipo_Garantia: 'PRENDARIA',
  Provincia: 'CHIRIQUI',
  Importe_Venta: 16440,
  prior_loan: DEFAULT_PRIOR_LOAN,
  prior_loans: 2,
  first_loan_start: '2022-01-10T00:00:00',
  days_past_due: 0,
  prior_defaults: 0,
  portfolio_segment: 'CARTERA NUEVA',
};

const PRIOR_STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'P', label: 'P - Liquidado' },
  { value: 'VIGENTE', label: 'VIGENTE - En curso' },
  { value: 'VENCIDO', label: 'VENCIDO - Incumplido' },
];

const PORTFOLIO_SEGMENT_OPTIONS = ['CARTERA NUEVA', 'CARTERA AJUSTADA', 'PREPANDEMIA'];

const prettyOption = (value: string): string => value.replace(/_/g, ' ');

const dateInputValue = (value?: string | null): string => (value ? value.slice(0, 10) : '');

const dateFromInput = (value: string): string | null => (value ? `${value}T00:00:00` : null);

interface RefinanceTabProps {
  onNotification: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

const RefinanceTab: React.FC<RefinanceTabProps> = ({ onNotification }) => {
  const [formData, setFormData] = useState<RefinanceInputData>(DEFAULT_FORM);
  const [includePriorLoan, setIncludePriorLoan] = useState(true);
  const [catalogues, setCatalogues] = useState<InputCatalogues | null>(null);
  const [ruleBook, setRuleBook] = useState<RefinanceRuleBook | null>(null);
  const [ruleBookError, setRuleBookError] = useState<string | null>(null);
  const [result, setResult] = useState<RefinanceResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [lookupId, setLookupId] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [loadedClient, setLoadedClient] = useState<RefinanceLookupResult | null>(null);
  const [section, setSection] = useState(0);

  useEffect(() => {
    let active = true;
    CreditRiskAPI.getInputCatalogues().then((response) => {
      if (active && response.success && response.data) {
        setCatalogues(response.data);
      }
    });
    CreditRiskAPI.getRefinanceRules().then((response) => {
      if (!active) return;
      if (response.success && response.data) {
        setRuleBook(response.data);
      } else {
        setRuleBookError(response.error?.message || 'No se pudo cargar el reglamento');
      }
    });
    return () => {
      active = false;
    };
  }, []);

  // Prefer the menus published with the rule book: those are model 3's own
  // encoders. The shared catalogues are first-match on models 1 and 2, so they
  // can offer a Producto this model never saw. Keep the current value in the
  // list either way, so a verified default cannot vanish from the control.
  const optionsFor = (field: keyof RefinanceInputData): string[] => {
    const fromRules = ruleBook?.fields?.[field as string]?.values || [];
    const fromShared = catalogues?.fields?.[field as string]?.values || [];
    const fromApi = fromRules.length ? fromRules : fromShared;
    const current = formData[field];
    const selected = current === undefined || current === null ? '' : String(current);
    if (selected && !fromApi.includes(selected)) return [...fromApi, selected];
    return fromApi;
  };

  const priorLoan = formData.prior_loan || DEFAULT_PRIOR_LOAN;

  const setField = (field: keyof RefinanceInputData, value: any) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const setPriorLoanField = (field: keyof RefinancePriorLoan, value: any) => {
    setFormData((current) => ({
      ...current,
      prior_loan: { ...(current.prior_loan || DEFAULT_PRIOR_LOAN), [field]: value },
    }));
  };

  const handleNumberChange = (field: keyof RefinanceInputData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setField(field, parseFloat(event.target.value) || 0);
  };

  // A blank has to reach the API as null rather than as 0: the rules read an
  // absent arrears figure as REQUIERE_DATOS and a 0 as "no arrears", so
  // collapsing the two would approve a client on a field nobody filled in.
  const handleOptionalNumberChange = (field: keyof RefinanceInputData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setField(field, value === '' ? null : parseFloat(value));
  };

  const handleSelectChange = (field: keyof RefinanceInputData) => (event: any) => {
    setField(field, event.target.value);
  };

  // An empty string counts as a value the rules can read, so "sin dato" has to
  // leave the form as null to come back NO_VERIFICABLE instead of CUMPLE.
  const handleOptionalSelectChange = (field: keyof RefinanceInputData) => (event: any) => {
    setField(field, event.target.value === '' ? null : event.target.value);
  };

  const buildPayload = (): RefinanceInputData => {
    if (includePriorLoan) return formData;
    // Without a previous loan the server counts the relationship off the rows
    // it was given, so a declared count or tenure could not change anything.
    return { ...formData, prior_loan: null, prior_loans: null, first_loan_start: null };
  };

  const handleLookup = async (forcedId?: string) => {
    const query = (forcedId ?? lookupId).trim();
    if (!query) {
      onNotification('Indique un ID de cliente o de crédito', 'warning');
      return;
    }
    if (forcedId) setLookupId(forcedId);
    setLookingUp(true);
    setResult(null);
    try {
      const response = await CreditRiskAPI.lookupRefinanceClient(query);
      if (!response.success || !response.data) {
        onNotification(response.error?.message || 'No se pudo buscar el expediente', 'error');
        return;
      }
      const lookup = response.data;
      setLoadedClient(lookup);
      if (!lookup.found || !lookup.application) {
        onNotification(lookup.note, 'warning');
        return;
      }
      setFormData({
        ...DEFAULT_FORM,
        ...lookup.application,
        prior_loan: lookup.application.prior_loan ?? null,
      });
      setIncludePriorLoan(Boolean(lookup.application.prior_loan));
      onNotification(
        `Cliente ${lookup.cliente_id} / crédito ${lookup.credito_id} cargado`,
        lookup.missing_fields.length ? 'warning' : 'success',
      );
    } catch (error) {
      onNotification('Failed to connect to the server', 'error');
    } finally {
      setLookingUp(false);
    }
  };

  const handleEvaluate = async () => {
    setLoading(true);
    setResult(null);

    try {
      const response = await CreditRiskAPI.predictRefinance(buildPayload());

      if (response.success && response.data) {
        setResult(response.data);
        onNotification('Evaluación de refinanciamiento completada', 'success');
      } else {
        onNotification(response.error?.message || 'La evaluación falló', 'error');
      }
    } catch (error) {
      onNotification('Failed to connect to the server', 'error');
    } finally {
      setLoading(false);
    }
  };

  const clearForm = () => {
    setFormData(DEFAULT_FORM);
    setIncludePriorLoan(true);
    setResult(null);
    setLookupId('');
    setLoadedClient(null);
  };

  const tier = result ? TIER_PRESENTATION[result.tier] || UNKNOWN_TIER : UNKNOWN_TIER;
  const actionLabel = result
    ? ACTION_LABELS[result.recommended_action] || prettyOption(result.recommended_action)
    : '';
  const hasScore = result != null && result.default_probability != null;
  const inputWarnings = result?.input_warnings || [];

  const renderRule = (rule: RefinanceEligibilityRule) => {
    const status = STATUS_PRESENTATION[rule.status] || STATUS_PRESENTATION.NO_VERIFICABLE;
    return (
      <Box
        key={rule.code}
        sx={{
          p: 1.5,
          borderRadius: 1,
          bgcolor: `${status.color}0d`,
          border: `1px ${status.dashed ? 'dashed' : 'solid'} ${status.color}66`,
          borderLeft: `4px solid ${status.color}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Box sx={{ color: status.color, display: 'flex' }}>{status.icon}</Box>
          <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
            {rule.code}
          </Typography>
          <Chip
            label={status.label}
            size="small"
            variant={status.dashed ? 'outlined' : 'filled'}
            sx={
              status.dashed
                ? { color: status.color, borderColor: status.color, borderStyle: 'dashed' }
                : { bgcolor: status.color, color: 'white' }
            }
          />
        </Box>
        <Typography variant="body2">{rule.requirement}</Typography>
        <Typography variant="caption" color="text.secondary">
          Observado: {rule.observed}
          {rule.status === 'NO_VERIFICABLE' && ' — no se pudo verificar, no equivale a incumplir'}
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Refinanciamiento
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        Las reglas deciden a quién se le puede ofrecer un refinanciamiento; el
        modelo solo pone precio cuando las reglas lo permiten.
      </Typography>

      <Tabs
        value={section}
        onChange={(_, value) => setSection(value)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Evaluar solicitud" />
        <Tab label="Lista SUMA" />
      </Tabs>

      <Box sx={{ display: section === 1 ? 'block' : 'none' }}>
        <SumaRefinanceList
          onOpenClient={(clienteId) => {
            setSection(0);
            handleLookup(clienteId);
          }}
        />
      </Box>

      <Box sx={{ display: section === 0 ? 'block' : 'none' }}>
      <Grid container spacing={3}>
        {/* Form Section */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3, height: 'fit-content' }}>
            <Typography variant="h6" gutterBottom color="primary">
              <Info sx={{ mr: 1, verticalAlign: 'middle' }} />
              Solicitud de Refinanciamiento
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
              Busque por ID de cliente o de crédito para precargar el expediente
              desde el extracto del Core. El monto pedido queda como renovación
              del crédito actual y se puede editar antes de evaluar.
            </Alert>
            <Grid container spacing={2} sx={{ mb: 1 }} alignItems="center">
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  label="ID de cliente o de crédito"
                  value={lookupId}
                  onChange={(event) => setLookupId(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleLookup();
                    }
                  }}
                  size="small"
                  placeholder="7001 o 300019129"
                />
              </Grid>
              <Grid item xs={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={lookingUp ? <CircularProgress size={16} /> : <Search />}
                  onClick={() => handleLookup()}
                  disabled={lookingUp || loading}
                >
                  {lookingUp ? 'Cargando...' : 'Cargar'}
                </Button>
              </Grid>
            </Grid>
            {loadedClient && (
              <Alert
                severity={
                  !loadedClient.found
                    ? 'warning'
                    : loadedClient.missing_fields.length
                      ? 'warning'
                      : loadedClient.estatus === 'VENCIDO'
                        ? 'error'
                        : 'success'
                }
                sx={{ mb: 2 }}
              >
                <Typography variant="body2">{loadedClient.note}</Typography>
                {loadedClient.found && (
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    Cliente {loadedClient.cliente_id} · Crédito {loadedClient.credito_id}
                    {loadedClient.estatus ? ` · ${loadedClient.estatus}` : ''}
                    {` · ${loadedClient.loans_on_file} crédito(s) en el extracto`}
                    {loadedClient.as_of ? ` · corte ${loadedClient.as_of.slice(0, 10)}` : ''}
                  </Typography>
                )}
                {loadedClient.missing_fields.length > 0 && (
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    Faltan en el extracto: {loadedClient.missing_fields.join(', ')}.
                    Complételos antes de evaluar.
                  </Typography>
                )}
              </Alert>
            )}

            {/* Requested Loan */}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }} color="primary">
              Crédito Solicitado
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Monto del Crédito (B/.)"
                  type="number"
                  value={formData.Monto_Credito}
                  onChange={handleNumberChange('Monto_Credito')}
                  size="small"
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label="Plazo (meses)"
                  type="number"
                  value={formData.Plazo}
                  onChange={handleNumberChange('Plazo')}
                  size="small"
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label="Tasa (%)"
                  type="number"
                  value={formData.Tasa_Fija}
                  onChange={handleNumberChange('Tasa_Fija')}
                  size="small"
                  inputProps={{ step: 0.1 }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Producto</InputLabel>
                  <Select
                    value={formData.Producto}
                    onChange={handleSelectChange('Producto')}
                    label="Producto"
                  >
                    {optionsFor('Producto').map((value) => (
                      <MenuItem key={value} value={value}>{prettyOption(value)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Destino del Crédito</InputLabel>
                  <Select
                    value={formData.Destino_Credito}
                    onChange={handleSelectChange('Destino_Credito')}
                    label="Destino del Crédito"
                  >
                    {optionsFor('Destino_Credito').map((value) => (
                      <MenuItem key={value} value={value}>{prettyOption(value)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo de Garantía</InputLabel>
                  <Select
                    value={formData.Tipo_Garantia}
                    onChange={handleSelectChange('Tipo_Garantia')}
                    label="Tipo de Garantía"
                  >
                    {(catalogues?.guarantee_types || [formData.Tipo_Garantia]).map((value) => (
                      <MenuItem key={value} value={value}>{prettyOption(value)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Applicant */}
            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }} color="primary">
              Solicitante
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Edad"
                  type="number"
                  value={formData.Edad}
                  onChange={handleNumberChange('Edad')}
                  size="small"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Estado Civil</InputLabel>
                  <Select
                    value={formData.Estado_Civil}
                    onChange={handleSelectChange('Estado_Civil')}
                    label="Estado Civil"
                  >
                    {optionsFor('Estado_Civil').map((value) => (
                      <MenuItem key={value} value={value}>{prettyOption(value)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo de Persona</InputLabel>
                  <Select
                    value={formData.Tipo_Persona}
                    onChange={handleSelectChange('Tipo_Persona')}
                    label="Tipo de Persona"
                  >
                    {optionsFor('Tipo_Persona').map((value) => (
                      <MenuItem key={value} value={value}>{prettyOption(value)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Ingreso Mensual (B/.)"
                  type="number"
                  value={formData.Ingreso_Mensual}
                  onChange={handleNumberChange('Ingreso_Mensual')}
                  size="small"
                  helperText="Los ingresos anuales se derivan como mensual × 12"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Ventas Anuales (B/.)"
                  type="number"
                  value={formData.Importe_Venta ?? ''}
                  onChange={handleOptionalNumberChange('Importe_Venta')}
                  size="small"
                  helperText="Opcional; en blanco = no declarado"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Provincia</InputLabel>
                  <Select
                    value={formData.Provincia}
                    onChange={handleSelectChange('Provincia')}
                    label="Provincia"
                  >
                    {optionsFor('Provincia').map((value) => (
                      <MenuItem key={value} value={value}>{prettyOption(value)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sucursal</InputLabel>
                  <Select
                    value={formData.Sucursal_Grupo}
                    onChange={handleSelectChange('Sucursal_Grupo')}
                    label="Sucursal"
                  >
                    {optionsFor('Sucursal_Grupo').map((value) => (
                      <MenuItem key={value} value={value}>{prettyOption(value)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Segmento (Sector General)</InputLabel>
                  <Select
                    value={formData.Sector_General}
                    onChange={handleSelectChange('Sector_General')}
                    label="Segmento (Sector General)"
                  >
                    {optionsFor('Sector_General').map((value) => (
                      <MenuItem key={value} value={value}>{prettyOption(value)}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Tamaño de empresa, no actividad económica</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sector Económico</InputLabel>
                  <Select
                    value={formData.Sector_Economico}
                    onChange={handleSelectChange('Sector_Economico')}
                    label="Sector Económico"
                  >
                    {optionsFor('Sector_Economico').map((value) => (
                      <MenuItem key={value} value={value}>{prettyOption(value)}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Actividad del negocio</FormHelperText>
                </FormControl>
              </Grid>
            </Grid>

            {/* Prior Loan */}
            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }} color="primary">
              Crédito Anterior
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={includePriorLoan}
                  onChange={(event) => setIncludePriorLoan(event.target.checked)}
                />
              }
              label="El cliente tiene un crédito anterior"
            />
            {includePriorLoan ? (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Monto Anterior (B/.)"
                    type="number"
                    value={priorLoan.amount}
                    onChange={(event) =>
                      setPriorLoanField('amount', parseFloat(event.target.value) || 0)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Plazo (meses)"
                    type="number"
                    value={priorLoan.term}
                    onChange={(event) =>
                      setPriorLoanField('term', parseFloat(event.target.value) || 0)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Tasa (%)"
                    type="number"
                    value={priorLoan.rate ?? ''}
                    onChange={(event) =>
                      setPriorLoanField(
                        'rate',
                        event.target.value === '' ? null : parseFloat(event.target.value)
                      )}
                    size="small"
                    inputProps={{ step: 0.1 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Producto Anterior</InputLabel>
                    <Select
                      value={priorLoan.product || ''}
                      onChange={(event) => setPriorLoanField('product', event.target.value)}
                      label="Producto Anterior"
                    >
                      {optionsFor('Producto').map((value) => (
                        <MenuItem key={value} value={value}>{prettyOption(value)}</MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>Se compara con el producto solicitado</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Estatus Anterior</InputLabel>
                    <Select
                      value={priorLoan.status || ''}
                      onChange={(event) => setPriorLoanField('status', event.target.value)}
                      label="Estatus Anterior"
                    >
                      {PRIOR_STATUS_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Fecha de Desembolso"
                    type="date"
                    value={dateInputValue(priorLoan.start)}
                    onChange={(event) =>
                      setPriorLoanField('start', dateFromInput(event.target.value))}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Fecha de Vencimiento"
                    type="date"
                    value={dateInputValue(priorLoan.end)}
                    onChange={(event) =>
                      setPriorLoanField('end', dateFromInput(event.target.value))}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    helperText="Opcional; se deriva del desembolso + plazo"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Atraso en la Primera Letra</InputLabel>
                    <Select
                      value={
                        priorLoan.first_instalment_late === null
                          || priorLoan.first_instalment_late === undefined
                          ? ''
                          : String(priorLoan.first_instalment_late)
                      }
                      onChange={(event) =>
                        setPriorLoanField(
                          'first_instalment_late',
                          event.target.value === '' ? null : event.target.value === 'true'
                        )}
                      label="Atraso en la Primera Letra"
                    >
                      <MenuItem value="false">No</MenuItem>
                      <MenuItem value="true">Sí</MenuItem>
                      <MenuItem value="">Sin dato</MenuItem>
                    </Select>
                    <FormHelperText>
                      Sin dato bloquea la decisión como REQUIERE_DATOS
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Créditos Previos"
                    type="number"
                    value={formData.prior_loans ?? ''}
                    onChange={handleOptionalNumberChange('prior_loans')}
                    size="small"
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Primer Crédito"
                    type="date"
                    value={dateInputValue(formData.first_loan_start)}
                    onChange={(event) =>
                      setField('first_loan_start', dateFromInput(event.target.value))}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    helperText="Antigüedad"
                  />
                </Grid>
              </Grid>
            ) : (
              <Alert severity="info" sx={{ mt: 1 }}>
                Sin crédito anterior el cliente queda FUERA_DE_ALCANCE: es una
                consulta de scorecard de nuevo cliente, no un rechazo.
              </Alert>
            )}

            {/* Rule inputs */}
            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }} color="primary">
              Situación Actual del Cliente
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Estos campos alimentan las reglas y no el modelo: son datos de hoy,
              y el modelo solo usa hechos fijados al momento del desembolso.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Días de Mora"
                  type="number"
                  value={formData.days_past_due ?? ''}
                  onChange={handleOptionalNumberChange('days_past_due')}
                  size="small"
                  helperText="En blanco = sin dato (REQUIERE_DATOS)"
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Créditos Vencidos"
                  type="number"
                  value={formData.prior_defaults ?? ''}
                  onChange={handleOptionalNumberChange('prior_defaults')}
                  size="small"
                  helperText="Anteriores terminados en VENCIDO"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Segmentación Cartera</InputLabel>
                  <Select
                    value={formData.portfolio_segment || ''}
                    onChange={handleOptionalSelectChange('portfolio_segment')}
                    label="Segmentación Cartera"
                  >
                    {(ruleBook?.portfolio_segments || PORTFOLIO_SEGMENT_OPTIONS).map((value) => (
                      <MenuItem key={value} value={value}>{value}</MenuItem>
                    ))}
                    <MenuItem value="">Sin dato</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleEvaluate}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <Autorenew />}
                fullWidth
                size="large"
              >
                {loading ? 'Evaluando...' : 'Evaluar Refinanciamiento'}
              </Button>
              <Button variant="outlined" onClick={clearForm} disabled={loading} size="large">
                Limpiar
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Results Section */}
        <Grid item xs={12} lg={6}>
          <Stack spacing={2}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom color="primary">
                <Policy sx={{ mr: 1, verticalAlign: 'middle' }} />
                Decisión de Elegibilidad
              </Typography>

              {result ? (
                <Box>
                  <Card sx={{ mb: 2, bgcolor: tier.color, color: 'white' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {tier.icon}
                        <Typography variant="h5" sx={{ flexGrow: 1 }}>
                          {tier.label}
                        </Typography>
                        <Chip
                          label={actionLabel}
                          sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                        />
                      </Box>
                      <Typography variant="body2">{tier.meaning}</Typography>
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.85 }}>
                        {result.tier} · {result.recommended_action}
                      </Typography>
                    </CardContent>
                  </Card>

                  {result.reasons.length > 0 && (
                    <Alert severity={tier.severity} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {result.reasons.length === 1
                          ? 'Una regla no se satisfizo'
                          : `${result.reasons.length} reglas no se satisficieron`}
                      </Typography>
                      <Stack spacing={0.5}>
                        {result.reasons.map((reason, index) => (
                          <Typography key={index} variant="caption" sx={{ display: 'block' }}>
                            • {reason}
                          </Typography>
                        ))}
                      </Stack>
                    </Alert>
                  )}

                  {inputWarnings.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {inputWarnings.length === 1
                          ? 'Un campo no se pudo usar tal como se envió'
                          : `${inputWarnings.length} campos no se pudieron usar tal como se enviaron`}
                      </Typography>
                      <Stack spacing={0.5}>
                        {inputWarnings.map((warning, index) => (
                          <Typography key={index} variant="caption" sx={{ display: 'block' }}>
                            • {warning}
                          </Typography>
                        ))}
                      </Stack>
                    </Alert>
                  )}

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    ID: {result.prediction_id} · Modelo: {result.model_version} ·{' '}
                    {new Date(result.timestamp).toLocaleString()}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  <Policy sx={{ fontSize: 60, mb: 2 }} />
                  <Typography>Evaluar la solicitud para ver la decisión</Typography>
                </Box>
              )}
            </Paper>

            {result && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  <RuleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Veredicto Regla por Regla
                </Typography>

                {KIND_ORDER.map((kind) => {
                  const rules = result.rules.filter((rule) => rule.kind === kind);
                  if (rules.length === 0) return null;
                  const presentation = KIND_PRESENTATION[kind];
                  return (
                    <Box key={kind} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip
                          label={presentation.label}
                          size="small"
                          sx={{ bgcolor: presentation.color, color: 'white' }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {presentation.explanation}
                        </Typography>
                      </Box>
                      <Stack spacing={1}>{rules.map(renderRule)}</Stack>
                    </Box>
                  );
                })}
              </Paper>
            )}

            {result && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  <Autorenew sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Riesgo del Refinanciamiento
                </Typography>

                {hasScore ? (
                  <Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Probabilidad de Incumplimiento
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LinearProgress
                          variant="determinate"
                          value={(result.default_probability || 0) * 100}
                          sx={{ flexGrow: 1, mr: 2, height: 10, borderRadius: 5 }}
                          color={
                            (result.default_probability || 0) < 0.2
                              ? 'success'
                              : (result.default_probability || 0) < 0.5
                                ? 'warning'
                                : 'error'
                          }
                        />
                        <Typography variant="h6">
                          {formatPercentage(result.default_probability || 0)}
                        </Typography>
                      </Box>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Puntaje
                        </Typography>
                        <Typography variant="h6">{result.credit_score ?? '—'}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Banda de Riesgo
                        </Typography>
                        <Typography variant="h6">{result.risk_band ?? '—'}</Typography>
                      </Grid>
                    </Grid>

                    {result.level_expired && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        El anclaje del nivel está vencido: el ordenamiento entre
                        clientes sigue siendo válido, pero este número no debe
                        citarse como probabilidad.
                      </Alert>
                    )}

                    {result.level_note && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 1 }}
                      >
                        {result.level_note}
                      </Typography>
                    )}

                    {result.anchored != null && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        {result.anchored
                          ? 'Nivel ajustado a la experiencia observada reciente.'
                          : 'Nivel sin ajuste a experiencia observada.'}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Alert severity={tier.severity} icon={tier.icon}>
                    <Typography variant="subtitle2" gutterBottom>
                      Sin probabilidad para esta solicitud
                    </Typography>
                    <Typography variant="body2">{tier.noScore}</Typography>
                  </Alert>
                )}
              </Paper>
            )}

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom color="primary">
                <Gavel sx={{ mr: 1, verticalAlign: 'middle' }} />
                Reglamento Publicado
              </Typography>

              {ruleBook ? (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Política determinista, no un modelo: cada regla es un umbral
                    publicado sobre un dato que el core ya tiene.
                  </Typography>

                  {KIND_ORDER.map((kind) => {
                    const rules = ruleBook.rules.filter((rule) => rule.kind === kind);
                    if (rules.length === 0) return null;
                    const presentation = KIND_PRESENTATION[kind];
                    return (
                      <Box key={kind} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Chip
                            label={presentation.label}
                            size="small"
                            variant="outlined"
                            sx={{ color: presentation.color, borderColor: presentation.color }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {presentation.explanation}
                          </Typography>
                        </Box>
                        {rules.map((rule) => (
                          <Typography key={rule.code} variant="body2" sx={{ ml: 1 }}>
                            • <strong>{rule.code}</strong>: {rule.requirement}
                          </Typography>
                        ))}
                      </Box>
                    );
                  })}

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" gutterBottom>
                    Umbrales
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Mora máxima: {ruleBook.thresholds.max_days_past_due} días
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Plazo anterior transcurrido:{' '}
                    {formatPercentage(ruleBook.thresholds.min_share_of_prior_term)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Antigüedad mínima: {ruleBook.thresholds.min_months_as_client} meses
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Monto máximo vs. anterior: {ruleBook.thresholds.max_amount_vs_prior}x
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Cartera reestructurada: {ruleBook.thresholds.restructured_segment}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" gutterBottom>
                    Niveles
                  </Typography>
                  <Stack spacing={0.5}>
                    {Object.entries(ruleBook.tiers).map(([name, meaning]) => {
                      const presentation = TIER_PRESENTATION[name as RefinanceTier] || UNKNOWN_TIER;
                      return (
                        <Box key={name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={presentation.label}
                            size="small"
                            sx={{ bgcolor: presentation.color, color: 'white', minWidth: 150 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {meaning}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" gutterBottom>
                    Evidencia Fuera de Tiempo
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Población: {ruleBook.measured.population}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Incumplimiento si elegible:{' '}
                    {formatPercentage(ruleBook.measured.default_rate_if_eligible)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Incumplimiento si rechazado:{' '}
                    {formatPercentage(ruleBook.measured.default_rate_if_rejected)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Aporte sobre el score: {ruleBook.measured.adds_beyond_the_score}
                  </Typography>
                </Box>
              ) : ruleBookError ? (
                <Alert severity="warning">{ruleBookError}</Alert>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Cargando reglamento...
                  </Typography>
                </Box>
              )}
            </Paper>
          </Stack>
        </Grid>
      </Grid>
      </Box>
    </Box>
  );
};

export default RefinanceTab;
